import type { ProductGroupSegment } from './report-product-groups.model';
import {
  PRODUCT_GROUPS_DONUT_CENTER_X_PERCENT,
  PRODUCT_GROUPS_DONUT_CENTER_Y_PERCENT,
  PRODUCT_GROUPS_DONUT_HEIGHT_PERCENT,
  PRODUCT_GROUPS_DONUT_OUTER_DIAMETER_PERCENT,
  PRODUCT_GROUPS_LEGEND_ICON_OFFSET_PERCENT,
  PRODUCT_GROUPS_LEGEND_LEFT_PERCENT,
  PRODUCT_GROUPS_LEGEND_LINE_CORNER_RADIUS_PERCENT,
  PRODUCT_GROUPS_LEGEND_LINE_ICON_GAP_PERCENT,
  PRODUCT_GROUPS_LEGEND_LINE_RADIAL_STUB_PERCENT,
  PRODUCT_GROUPS_LEGEND_ROW_SPACING_PCT,
} from './report-product-groups.constants';
import {
  buildProductGroupDonutSliceGeometries,
  donutViewBoxPointToPagePercent,
  getDonutSliceAnchorViewBox,
  getProductGroupSliceFlatOuterArcRangeDeg,
  PRODUCT_GROUPS_DONUT_INNER_RADIUS_VB,
  PRODUCT_GROUPS_DONUT_REFERENCE_OUTER_RADIUS_VB,
  PRODUCT_GROUPS_DONUT_SLICE_GAP_DEG,
  type BuildProductGroupDonutSliceGeometriesOptions,
  type ProductGroupDonutSliceGeometry,
} from './product-groups-donut.helpers';
import { placeProductGroupsLegendRows } from './product-groups-legend.helpers';

export type LeaderLinePoint = { leftPct: number; topPct: number };

export type ProductGroupLeaderLineLayout = {
  id: string;
  /** Page-relative % polyline — radial stub, circular track arc, then horizontal. */
  points: readonly LeaderLinePoint[];
};

const DONUT_CENTER: LeaderLinePoint = {
  leftPct: PRODUCT_GROUPS_DONUT_CENTER_X_PERCENT,
  topPct: PRODUCT_GROUPS_DONUT_CENTER_Y_PERCENT,
};

const OUTER_RX_PCT = PRODUCT_GROUPS_DONUT_OUTER_DIAMETER_PERCENT / 2;
const OUTER_RY_PCT = PRODUCT_GROUPS_DONUT_HEIGHT_PERCENT / 2;

const DONUT_INNER_RADIUS_SCALE =
  PRODUCT_GROUPS_DONUT_INNER_RADIUS_VB /
  PRODUCT_GROUPS_DONUT_REFERENCE_OUTER_RADIUS_VB;

/** Baseline outer ring — slightly outside the donut outer edge. */
const BASELINE_OUTER_RADIUS_SCALE =
  1 + (PRODUCT_GROUPS_DONUT_SLICE_GAP_DEG / 360) * 0.25;

/** Radial gap between concentric leader-line tracks — one donut slice gap (vb arc at baseline). */
const SLICE_GAP_ARC_LENGTH_VB =
  PRODUCT_GROUPS_DONUT_REFERENCE_OUTER_RADIUS_VB *
  ((PRODUCT_GROUPS_DONUT_SLICE_GAP_DEG * Math.PI) / 180);

export const LEADER_LINE_TRACK_GAP_RADIUS_SCALE =
  (SLICE_GAP_ARC_LENGTH_VB / PRODUCT_GROUPS_DONUT_REFERENCE_OUTER_RADIUS_VB) *
  1;

/** Track {@link lane} — {@link lane} slice-gap steps outside the baseline outer radius. */
export function getLeaderLineTrackRadiusScaleForLane(lane: number): number {
  return (
    BASELINE_OUTER_RADIUS_SCALE + lane * LEADER_LINE_TRACK_GAP_RADIUS_SCALE
  );
}

const ARC_SAMPLE_COUNT = 14;

function pagePointFromEllipseAngle(
  angleRad: number,
  radiusScale: number,
): LeaderLinePoint {
  return {
    leftPct:
      DONUT_CENTER.leftPct + OUTER_RX_PCT * radiusScale * Math.cos(angleRad),
    topPct:
      DONUT_CENTER.topPct + OUTER_RY_PCT * radiusScale * Math.sin(angleRad),
  };
}

/** Parametric polar angle on the donut ellipse (page % space). */
export function getEllipseAngleFromPagePoint(point: LeaderLinePoint): number {
  const dx = point.leftPct - DONUT_CENTER.leftPct;
  const dy = point.topPct - DONUT_CENTER.topPct;
  return Math.atan2(dy / OUTER_RY_PCT, dx / OUTER_RX_PCT);
}

export function getEllipseRadiusScale(point: LeaderLinePoint): number {
  const dx = point.leftPct - DONUT_CENTER.leftPct;
  const dy = point.topPct - DONUT_CENTER.topPct;
  const nx = dx / OUTER_RX_PCT;
  const ny = dy / OUTER_RY_PCT;
  return Math.hypot(nx, ny);
}

function isPointInsideDonutHole(point: LeaderLinePoint): boolean {
  const dx = point.leftPct - DONUT_CENTER.leftPct;
  const dy = point.topPct - DONUT_CENTER.topPct;
  const nx = dx / (OUTER_RX_PCT * DONUT_INNER_RADIUS_SCALE);
  const ny = dy / (OUTER_RY_PCT * DONUT_INNER_RADIUS_SCALE);
  return nx * nx + ny * ny < 1 - 1e-9;
}

function segmentIntersectsDonutHole(
  a: LeaderLinePoint,
  b: LeaderLinePoint,
): boolean {
  if (isPointInsideDonutHole(a) || isPointInsideDonutHole(b)) return true;

  const samples = 12;
  for (let i = 1; i < samples; i++) {
    const t = i / samples;
    if (
      isPointInsideDonutHole({
        leftPct: a.leftPct + t * (b.leftPct - a.leftPct),
        topPct: a.topPct + t * (b.topPct - a.topPct),
      })
    ) {
      return true;
    }
  }

  return false;
}

/** Every segment must stay outside the donut inner hole. */
export function leaderLineAvoidsDonutHole(
  points: readonly LeaderLinePoint[],
): boolean {
  for (let i = 1; i < points.length; i++) {
    if (segmentIntersectsDonutHole(points[i - 1]!, points[i]!)) return false;
  }
  return true;
}

function normalizeAngleDelta(delta: number): number {
  let d = delta % (2 * Math.PI);
  if (d <= -Math.PI) d += 2 * Math.PI;
  if (d > Math.PI) d -= 2 * Math.PI;
  return d;
}

/** Shortest signed sweep (radians) from {@link startAngle} to {@link endAngle}. */
export function getShortestArcSweepRadians(
  startAngle: number,
  endAngle: number,
): number {
  return normalizeAngleDelta(endAngle - startAngle);
}

function sampleEllipseArc(
  startAngle: number,
  endAngle: number,
  radiusScale: number,
): LeaderLinePoint[] {
  const sweep = getShortestArcSweepRadians(startAngle, endAngle);
  const points: LeaderLinePoint[] = [];

  for (let i = 0; i <= ARC_SAMPLE_COUNT; i++) {
    const t = i / ARC_SAMPLE_COUNT;
    points.push(pagePointFromEllipseAngle(startAngle + sweep * t, radiusScale));
  }

  return points;
}

/**
 * Right-side angle on {@link radiusScale} whose page Y matches {@link rowTopPct}.
 */
function getTargetAngleForRow(
  rowTopPct: number,
  radiusScale: number,
): number | undefined {
  const ryTrack = OUTER_RY_PCT * radiusScale;
  const sinTheta = (rowTopPct - DONUT_CENTER.topPct) / ryTrack;
  if (Math.abs(sinTheta) > 1 + 1e-6) return undefined;
  return Math.asin(Math.max(-1, Math.min(1, sinTheta)));
}

function segmentsIntersect2D(
  a1: LeaderLinePoint,
  a2: LeaderLinePoint,
  b1: LeaderLinePoint,
  b2: LeaderLinePoint,
): boolean {
  const d1x = a2.leftPct - a1.leftPct;
  const d1y = a2.topPct - a1.topPct;
  const d2x = b2.leftPct - b1.leftPct;
  const d2y = b2.topPct - b1.topPct;
  const denom = d1x * d2y - d1y * d2x;
  if (Math.abs(denom) < 1e-12) return false;

  const t =
    ((b1.leftPct - a1.leftPct) * d2y - (b1.topPct - a1.topPct) * d2x) / denom;
  const u =
    ((b1.leftPct - a1.leftPct) * d1y - (b1.topPct - a1.topPct) * d1x) / denom;
  const eps = 1e-4;

  return t > eps && t < 1 - eps && u > eps && u < 1 - eps;
}

export function leaderLinesIntersect(
  a: readonly LeaderLinePoint[],
  b: readonly LeaderLinePoint[],
): boolean {
  for (let i = 1; i < a.length; i++) {
    for (let j = 1; j < b.length; j++) {
      if (segmentsIntersect2D(a[i - 1]!, a[i]!, b[j - 1]!, b[j]!)) {
        return true;
      }
    }
  }
  return false;
}

function getLegendRowTopPct(labelTopPct: number): number {
  return (
    PRODUCT_GROUPS_DONUT_CENTER_Y_PERCENT -
    PRODUCT_GROUPS_DONUT_HEIGHT_PERCENT / 2 +
    (labelTopPct / 100) * PRODUCT_GROUPS_DONUT_HEIGHT_PERCENT
  );
}

/**
 * Outward radial unit from donut center through {@link anchor} (page % space).
 */
function radialUnitFromCenter(
  anchor: LeaderLinePoint,
): LeaderLinePoint | undefined {
  const dx = anchor.leftPct - DONUT_CENTER.leftPct;
  const dy = anchor.topPct - DONUT_CENTER.topPct;
  const length = Math.hypot(dx, dy);
  if (length <= 0) return undefined;
  return { leftPct: dx / length, topPct: dy / length };
}

/** Short outward stub from the slice rim. */
function computeMinimumRadialStubBend(
  anchor: LeaderLinePoint,
): LeaderLinePoint {
  const unit = radialUnitFromCenter(anchor);
  if (!unit) {
    return {
      leftPct: anchor.leftPct + PRODUCT_GROUPS_LEGEND_LINE_RADIAL_STUB_PERCENT,
      topPct: anchor.topPct,
    };
  }

  const anchorDist = Math.hypot(
    anchor.leftPct - DONUT_CENTER.leftPct,
    anchor.topPct - DONUT_CENTER.topPct,
  );
  const minOutwardDist =
    anchorDist + PRODUCT_GROUPS_LEGEND_LINE_RADIAL_STUB_PERCENT;

  return {
    leftPct: DONUT_CENTER.leftPct + minOutwardDist * unit.leftPct,
    topPct: DONUT_CENTER.topPct + minOutwardDist * unit.topPct,
  };
}

/** No point may sit right of the icon anchor; the final run approaches from the left. */
export function leaderLineStaysLeftOfLabel(
  points: readonly LeaderLinePoint[],
  labelEndLeftPct: number,
): boolean {
  if (points.length < 2) return false;

  for (let i = 1; i < points.length; i++) {
    if (points[i]!.leftPct > labelEndLeftPct + 1e-6) return false;
  }

  const beforeLabel = points.at(-2)!;
  const labelEnd = points.at(-1)!;
  if (beforeLabel.leftPct > labelEnd.leftPct + 1e-6) return false;

  return true;
}

type LeaderLineRequest = {
  id: string;
  anchor: LeaderLinePoint;
  minTrackLane: number;
  rowTopPct: number;
  labelEndLeftPct: number;
  /** Distance (in rows) from the legend list center — smaller means more central. */
  listCenterDistance: number;
};

function getMinimumTrackLaneForAnchor(anchor: LeaderLinePoint): number {
  const radialStub = computeMinimumRadialStubBend(anchor);
  const minTrackRadiusScale = Math.max(
    getEllipseRadiusScale(radialStub),
    getEllipseRadiusScale(anchor),
  );
  if (minTrackRadiusScale <= getLeaderLineTrackRadiusScaleForLane(1) + 1e-6) {
    return 1;
  }

  return Math.ceil(
    (minTrackRadiusScale - BASELINE_OUTER_RADIUS_SCALE) /
      LEADER_LINE_TRACK_GAP_RADIUS_SCALE,
  );
}

function getMaxTrackRadiusScale(
  rowTopPct: number,
  labelEndLeftPct: number,
): number | undefined {
  const targetAngle = getTargetAngleForRow(rowTopPct, 1);
  if (targetAngle === undefined) return undefined;

  const cosTheta = Math.cos(targetAngle);
  if (cosTheta <= 1e-6) return undefined;

  const maxScale =
    (labelEndLeftPct - DONUT_CENTER.leftPct) / (OUTER_RX_PCT * cosTheta);
  return maxScale > 0 ? maxScale : undefined;
}

const LEGEND_ROW_CENTER_SPACING_TOP_PCT =
  (PRODUCT_GROUPS_LEGEND_ROW_SPACING_PCT / 100) *
  PRODUCT_GROUPS_DONUT_HEIGHT_PERCENT;

function routesShareLegendCorridor(
  rowTopPctA: number,
  rowTopPctB: number,
): boolean {
  return (
    Math.abs(rowTopPctA - rowTopPctB) <=
    LEGEND_ROW_CENTER_SPACING_TOP_PCT * 1.05
  );
}

function routesOverlapRequests(
  a: LeaderLineRequest,
  b: LeaderLineRequest,
): boolean {
  if (!routesShareLegendCorridor(a.rowTopPct, b.rowTopPct)) return false;

  const targetAngleA = getTargetAngleForRow(
    a.rowTopPct,
    getLeaderLineTrackRadiusScaleForLane(a.minTrackLane),
  );
  const targetAngleB = getTargetAngleForRow(
    b.rowTopPct,
    getLeaderLineTrackRadiusScaleForLane(b.minTrackLane),
  );
  if (targetAngleA === undefined || targetAngleB === undefined) return false;

  return arcIntervalsOverlap(
    getEllipseAngleFromPagePoint(a.anchor),
    targetAngleA,
    getEllipseAngleFromPagePoint(b.anchor),
    targetAngleB,
  );
}

function getRouteOverlapCluster(
  seed: LeaderLineRequest,
  requests: readonly LeaderLineRequest[],
): LeaderLineRequest[] {
  const clusterById = new Map<string, LeaderLineRequest>([[seed.id, seed]]);
  const queue = [seed];

  while (queue.length > 0) {
    const current = queue.pop()!;
    const currentAngle = getEllipseAngleFromPagePoint(current.anchor);

    for (const other of requests) {
      if (clusterById.has(other.id)) continue;

      if (routesOverlapRequests(current, other)) {
        clusterById.set(other.id, other);
        queue.push(other);
        continue;
      }

      if (!routesShareLegendCorridor(current.rowTopPct, other.rowTopPct)) {
        continue;
      }

      const otherAngle = getEllipseAngleFromPagePoint(other.anchor);
      if (currentAngle < otherAngle) {
        clusterById.set(other.id, other);
        queue.push(other);
      }
    }
  }

  return [...clusterById.values()];
}

function arcIntervalsOverlap(
  anchorAngleA: number,
  targetAngleA: number,
  anchorAngleB: number,
  targetAngleB: number,
): boolean {
  const sweepA = getShortestArcSweepRadians(anchorAngleA, targetAngleA);
  const sweepB = getShortestArcSweepRadians(anchorAngleB, targetAngleB);
  const samples = 8;

  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const angleA = anchorAngleA + sweepA * t;
    const angleB = anchorAngleB + sweepB * t;
    if (Math.abs(normalizeAngleDelta(angleA - angleB)) < 0.35) return true;
  }

  return false;
}

function buildLeaderLinePointsOnTrack(
  anchor: LeaderLinePoint,
  rowTopPct: number,
  labelEndLeftPct: number,
  trackRadiusScale: number,
): LeaderLinePoint[] | undefined {
  const anchorAngle = getEllipseAngleFromPagePoint(anchor);
  const targetAngle = getTargetAngleForRow(rowTopPct, trackRadiusScale);
  if (targetAngle === undefined) return undefined;

  const radialEnd = pagePointFromEllipseAngle(anchorAngle, trackRadiusScale);
  const arcEnd = pagePointFromEllipseAngle(targetAngle, trackRadiusScale);
  if (arcEnd.leftPct > labelEndLeftPct + 1e-6) return undefined;

  const labelEnd: LeaderLinePoint = {
    leftPct: labelEndLeftPct,
    topPct: rowTopPct,
  };

  const arcPoints = sampleEllipseArc(
    anchorAngle,
    targetAngle,
    trackRadiusScale,
  );

  const points = [anchor, radialEnd, ...arcPoints.slice(1), labelEnd];

  if (!leaderLineStaysLeftOfLabel(points, labelEndLeftPct)) return undefined;
  if (!leaderLineAvoidsDonutHole(points)) return undefined;

  return points;
}

const MAX_TRACK_LANE_SEARCH_SPAN = 10;

/**
 * Direct radial-stub → horizontal path with a single bend, no circular-track
 * arc. Used when the anchor's radial ray reaches the row's height just outside
 * the slice and left of the label, so the tiny kink a near-zero arc sweep would
 * add is avoided. The bend is where the anchor's radial ray (same ellipse angle,
 * larger scale) crosses the row's horizontal line. Returns undefined when the
 * row is angularly far enough that a circular-track arc is required instead.
 */
function buildDirectLeaderLinePoints(
  anchor: LeaderLinePoint,
  rowTopPct: number,
  labelEndLeftPct: number,
): LeaderLinePoint[] | undefined {
  const labelEnd: LeaderLinePoint = {
    leftPct: labelEndLeftPct,
    topPct: rowTopPct,
  };
  const anchorAngle = getEllipseAngleFromPagePoint(anchor);
  const anchorScale = getEllipseRadiusScale(anchor);
  const sinAngle = Math.sin(anchorAngle);

  const validate = (
    points: LeaderLinePoint[],
  ): LeaderLinePoint[] | undefined => {
    if (points.some((point) => point.leftPct > labelEndLeftPct + 1e-6)) {
      return undefined;
    }
    if (!leaderLineStaysLeftOfLabel(points, labelEndLeftPct)) return undefined;
    if (!leaderLineAvoidsDonutHole(points)) return undefined;
    return points;
  };

  // Near 3/9 o'clock the radial is ~horizontal: the anchor already sits at the
  // row's height, so a single straight segment reaches the label with no bend.
  if (Math.abs(sinAngle) < 1e-3) return validate([anchor, labelEnd]);

  // Bend = where the anchor's outward radial ray meets the row's horizontal
  // line (bend.topPct === rowTopPct by construction, so the run to the label is
  // exactly horizontal).
  const bendScale =
    (rowTopPct - DONUT_CENTER.topPct) / (OUTER_RY_PCT * sinAngle);

  // Row lies angularly beyond the slice (closer to center than the anchor): the
  // radial ray would have to cut back through the slice, so an arc is required.
  if (bendScale < anchorScale - 1e-3) return undefined;

  // Anchor already on the row's height → straight horizontal run, no stub.
  if (bendScale <= anchorScale + 1e-3) return validate([anchor, labelEnd]);

  const bend = pagePointFromEllipseAngle(anchorAngle, bendScale);
  return validate([anchor, bend, labelEnd]);
}

/**
 * Innermost buildable route whose line clears the label, the donut hole, and
 * every already-placed {@link obstacles} line. Prefers a direct radial→
 * horizontal path (no arc); only when that is unavailable or crosses does it
 * step outward through the circular-track lanes, then falls back to the first
 * buildable crossing lane and finally the base builder.
 */
function placeMemberOnInnermostClearLane(
  member: LeaderLineRequest,
  minLane: number,
  maxTrackRadiusScale: number,
  obstacles: readonly ProductGroupLeaderLineLayout[],
): LeaderLinePoint[] | undefined {
  const clears = (candidate: readonly LeaderLinePoint[]) =>
    !obstacles.some((line) => leaderLinesIntersect(line.points, candidate));

  const direct = buildDirectLeaderLinePoints(
    member.anchor,
    member.rowTopPct,
    member.labelEndLeftPct,
  );
  if (direct && clears(direct)) return direct;

  let crossingFallback: LeaderLinePoint[] | undefined;

  for (
    let lane = minLane;
    lane <= minLane + MAX_TRACK_LANE_SEARCH_SPAN;
    lane++
  ) {
    const trackRadiusScale = getLeaderLineTrackRadiusScaleForLane(lane);
    if (trackRadiusScale > maxTrackRadiusScale + 1e-6) break;

    const points = buildLeaderLinePointsOnTrack(
      member.anchor,
      member.rowTopPct,
      member.labelEndLeftPct,
      trackRadiusScale,
    );
    if (!points) continue;

    if (clears(points)) return points;
    if (!crossingFallback) crossingFallback = points;
  }

  if (crossingFallback) return crossingFallback;
  if (direct) return direct;

  return buildLeaderLinePoints(
    member.anchor,
    member.rowTopPct,
    member.labelEndLeftPct,
    { trackLane: minLane },
  );
}

/**
 * Nest a corridor cluster by list centrality: process the most central rows
 * first so they take inner tracks; each following (less central) line moves
 * outward to the first lane that clears every line already placed. Overlapping
 * lines therefore nest central→inner, while non-overlapping lines reuse inner
 * lanes.
 */
function placeRouteOverlapCluster(
  cluster: readonly LeaderLineRequest[],
  placed: ProductGroupLeaderLineLayout[],
): ProductGroupLeaderLineLayout[] {
  const clusterIds = new Set(cluster.map((member) => member.id));
  const placedOutsideCluster = placed.filter(
    (line) => !clusterIds.has(line.id),
  );
  const sortedCluster = [...cluster].sort((a, b) => {
    const centerDelta = a.listCenterDistance - b.listCenterDistance;
    if (Math.abs(centerDelta) > 1e-9) return centerDelta;
    return (
      getEllipseAngleFromPagePoint(b.anchor) -
      getEllipseAngleFromPagePoint(a.anchor)
    );
  });

  const updated: ProductGroupLeaderLineLayout[] = [];

  for (const member of sortedCluster) {
    const maxTrackRadiusScale = getMaxTrackRadiusScale(
      member.rowTopPct,
      member.labelEndLeftPct,
    );
    if (maxTrackRadiusScale === undefined) continue;

    const points = placeMemberOnInnermostClearLane(
      member,
      member.minTrackLane,
      maxTrackRadiusScale,
      [...placedOutsideCluster, ...updated],
    );
    if (!points) continue;

    updated.push({ id: member.id, points });
  }

  return updated;
}

/**
 * Radial stub onto a circular track, arc along the track, then horizontal into
 * the legend row.
 */
export function buildLeaderLinePoints(
  anchor: LeaderLinePoint,
  rowTopPct: number,
  labelEndLeftPct: number,
  options?: { trackRadiusScale?: number; trackLane?: number },
): LeaderLinePoint[] | undefined {
  const minTrackLane = getMinimumTrackLaneForAnchor(anchor);
  const trackRadiusScale =
    options?.trackRadiusScale ??
    getLeaderLineTrackRadiusScaleForLane(
      Math.max(minTrackLane, options?.trackLane ?? 1),
    );

  return buildLeaderLinePointsOnTrack(
    anchor,
    rowTopPct,
    labelEndLeftPct,
    trackRadiusScale,
  );
}

/**
 * Anchor a slice's leader line at the point on its flat outer arc nearest the
 * legend row's vertical level, clamped so it never lands in the rounded-corner
 * zone. Converts between path degrees and page-ellipse angle via the chart's
 * −90° rotation (page-ellipse angle = path angle − 90°).
 */
function getSliceLeaderAnchor(
  geometry: ProductGroupDonutSliceGeometry,
  rowTopPct: number,
): LeaderLinePoint {
  // Flat arc range must match the outer edge the line actually attaches to
  // (leaderOuterRadiusVb). Using proposalOuterRadiusVb breaks when the proposal
  // collapses to the inner ring (cloudValue 0) while the baseline still shows
  // at the reference outer radius — corner inset vanishes and the anchor lands
  // in the gap-adjacent corner zone.
  const flat = getProductGroupSliceFlatOuterArcRangeDeg({
    startAngle: geometry.startAngle,
    endAngle: geometry.endAngle,
    proposalOuterRadiusVb: geometry.leaderOuterRadiusVb,
  });
  // Keep the anchor twice the corner padding away from each slice edge so it
  // connects well clear of the rounded corners.
  const cornerPadStartDeg = flat.startDeg - geometry.startAngle;
  const cornerPadEndDeg = geometry.endAngle - flat.endDeg;
  let insetStartDeg = flat.startDeg + cornerPadStartDeg;
  let insetEndDeg = flat.endDeg - cornerPadEndDeg;
  if (insetStartDeg > insetEndDeg) {
    const midDeg = (geometry.startAngle + geometry.endAngle) / 2;
    insetStartDeg = midDeg;
    insetEndDeg = midDeg;
  }

  const windowLowDeg = insetStartDeg - 90;
  const windowHighDeg = insetEndDeg - 90;
  const windowMidDeg = (windowLowDeg + windowHighDeg) / 2;

  const anchorRadiusScale =
    geometry.leaderOuterRadiusVb /
    PRODUCT_GROUPS_DONUT_REFERENCE_OUTER_RADIUS_VB;
  const targetAngleRad =
    getTargetAngleForRow(rowTopPct, anchorRadiusScale) ?? 0;

  let targetDeg = (targetAngleRad * 180) / Math.PI;
  targetDeg += 360 * Math.round((windowMidDeg - targetDeg) / 360);

  const anchorEllipseDeg = Math.min(
    windowHighDeg,
    Math.max(windowLowDeg, targetDeg),
  );
  const anchorPathDeg = anchorEllipseDeg + 90;

  const anchorVb = getDonutSliceAnchorViewBox(
    anchorPathDeg,
    geometry.leaderOuterRadiusVb,
  );
  return donutViewBoxPointToPagePercent(anchorVb.x, anchorVb.y);
}

/**
 * Leader lines from each donut slice to its legend row — radial outward, arc on
 * a concentric track outside the donut, then horizontal into the label.
 */
export function buildProductGroupLeaderLines(
  segments: ProductGroupSegment[],
  options?: BuildProductGroupDonutSliceGeometriesOptions,
): ProductGroupLeaderLineLayout[] {
  const rows = placeProductGroupsLegendRows(segments);
  const geometries = buildProductGroupDonutSliceGeometries(segments, options);
  const geometryById = new Map(
    geometries.map((geometry) => [geometry.id, geometry]),
  );
  const iconLeftPct =
    PRODUCT_GROUPS_LEGEND_LEFT_PERCENT +
    PRODUCT_GROUPS_LEGEND_ICON_OFFSET_PERCENT;
  const lineEndPct = iconLeftPct - PRODUCT_GROUPS_LEGEND_LINE_ICON_GAP_PERCENT;

  const requests: LeaderLineRequest[] = [];
  const listCenterIndex = (rows.length - 1) / 2;

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex]!;
    const geometry = geometryById.get(row.id);
    if (!geometry) continue;

    const rowTopPct = getLegendRowTopPct(row.labelTopPct);
    const anchor = getSliceLeaderAnchor(geometry, rowTopPct);

    requests.push({
      id: row.id,
      anchor,
      minTrackLane: getMinimumTrackLaneForAnchor(anchor),
      rowTopPct,
      labelEndLeftPct: lineEndPct,
      listCenterDistance: Math.abs(rowIndex - listCenterIndex),
    });
  }

  const sortedRequests = [...requests].sort(
    (a, b) => a.rowTopPct - b.rowTopPct,
  );
  const placed: ProductGroupLeaderLineLayout[] = [];
  const processedIds = new Set<string>();

  for (const request of sortedRequests) {
    if (processedIds.has(request.id)) continue;

    const cluster = getRouteOverlapCluster(request, requests);
    const clusterLines = placeRouteOverlapCluster(cluster, placed);

    for (const line of clusterLines) {
      processedIds.add(line.id);
      const existingIndex = placed.findIndex((item) => item.id === line.id);
      if (existingIndex >= 0) placed[existingIndex] = line;
      else placed.push(line);
    }
  }

  return placed;
}

function pointTowards(
  from: LeaderLinePoint,
  to: LeaderLinePoint,
  distance: number,
): LeaderLinePoint {
  const dx = to.leftPct - from.leftPct;
  const dy = to.topPct - from.topPct;
  const length = Math.hypot(dx, dy);
  if (length <= 1e-9) return from;
  const t = Math.min(distance, length / 2) / length;
  return {
    leftPct: from.leftPct + dx * t,
    topPct: from.topPct + dy * t,
  };
}

/**
 * SVG path `d` for a leader line: the radial stub and legend run stay straight,
 * while the middle track segment is drawn as a true elliptical arc (`A`) so it
 * renders smooth regardless of the SVG's non-uniform aspect scaling. Each bend
 * (stub↔arc and arc↔legend run) is softened with a quadratic fillet. Falls back
 * to a straight polyline for degenerate lines without an arc segment.
 */
export function buildLeaderLinePathD(
  points: readonly LeaderLinePoint[],
): string {
  const moveTo = (point: LeaderLinePoint) =>
    `M ${point.leftPct} ${point.topPct}`;
  const lineTo = (point: LeaderLinePoint) =>
    `L ${point.leftPct} ${point.topPct}`;
  const quadTo = (control: LeaderLinePoint, end: LeaderLinePoint) =>
    `Q ${control.leftPct} ${control.topPct} ${end.leftPct} ${end.topPct}`;

  // Direct radial→horizontal path: soften the single bend with a fillet.
  if (points.length === 3) {
    const [start, bend, end] = points;
    if (!start || !bend || !end) return '';
    const r = PRODUCT_GROUPS_LEGEND_LINE_CORNER_RADIUS_PERCENT;
    const filletStart = pointTowards(bend, start, r);
    const filletEnd = pointTowards(bend, end, r);
    return [
      moveTo(start),
      lineTo(filletStart),
      quadTo(bend, filletEnd),
      lineTo(end),
    ].join(' ');
  }

  if (points.length < 5) {
    const [first, ...rest] = points;
    if (!first) return '';
    return [moveTo(first), ...rest.map(lineTo)].join(' ');
  }

  const anchor = points[0]!;
  const arcStart = points[1]!;
  const arcSecond = points[2]!;
  const arcPenultimate = points[points.length - 3]!;
  const arcEnd = points[points.length - 2]!;
  const labelEnd = points[points.length - 1]!;

  const r = PRODUCT_GROUPS_LEGEND_LINE_CORNER_RADIUS_PERCENT;

  const trackRadiusScale = getEllipseRadiusScale(arcStart);
  const rx = OUTER_RX_PCT * trackRadiusScale;
  const ry = OUTER_RY_PCT * trackRadiusScale;

  const sweep = getShortestArcSweepRadians(
    getEllipseAngleFromPagePoint(arcStart),
    getEllipseAngleFromPagePoint(arcEnd),
  );
  const sweepFlag = sweep >= 0 ? 1 : 0;
  const largeArcFlag = Math.abs(sweep) > Math.PI ? 1 : 0;

  // Back off from each bend so a fillet replaces the sharp vertex.
  const stubFilletStart = pointTowards(arcStart, anchor, r);
  const arcFilletStart = pointTowards(arcStart, arcSecond, r);
  const arcFilletEnd = pointTowards(arcEnd, arcPenultimate, r);
  const runFilletEnd = pointTowards(arcEnd, labelEnd, r);

  return [
    moveTo(anchor),
    lineTo(stubFilletStart),
    quadTo(arcStart, arcFilletStart),
    `A ${rx} ${ry} 0 ${largeArcFlag} ${sweepFlag} ${arcFilletEnd.leftPct} ${arcFilletEnd.topPct}`,
    quadTo(arcEnd, runFilletEnd),
    lineTo(labelEnd),
  ].join(' ');
}

/** True when {@link point} lies on the outward ray from center through {@link anchor}. */
export function isOnOutwardRadial(
  anchor: LeaderLinePoint,
  point: LeaderLinePoint,
): boolean {
  const unit = radialUnitFromCenter(anchor);
  if (!unit) return false;

  const vx = point.leftPct - anchor.leftPct;
  const vy = point.topPct - anchor.topPct;
  const cross = unit.leftPct * vy - unit.topPct * vx;
  return Math.abs(cross) < 0.05;
}

/** True when {@link point} lies on the donut ellipse at {@link radiusScale}. */
export function isOnEllipseTrack(
  point: LeaderLinePoint,
  radiusScale: number,
  tolerance = 0.04,
): boolean {
  return Math.abs(getEllipseRadiusScale(point) - radiusScale) < tolerance;
}
